/**
 * Schools marked as moved to another region.
 *
 * A school copied to the other region can stay here as a read-only copy, and
 * the dashboard-api refuses anything that would add learners, classes or
 * uploads to it with `400 { code: "E_SCHOOL_MOVED", region, error: { title,
 * body } }`. Most screens report a failed save with their own generic toast,
 * so this says why, once, whichever screen made the request.
 */

import { toast } from "vue3-toastify";

export const SCHOOL_MOVED_CODE = "E_SCHOOL_MOVED";

/** Several requests often fail together; one explanation is enough. */
const REPEAT_AFTER_MS = 5000;
let lastShownAt = 0;

type MovedBody = { code?: string; region?: string; error?: { body?: string } | string };

/** True for an axios error whose response says the school has moved region. */
export function isSchoolMovedError(error: unknown): boolean {
  const data = (error as { response?: { data?: MovedBody } })?.response?.data;
  return data?.code === SCHOOL_MOVED_CODE;
}

export function explainSchoolMoved(error: unknown): void {
  if (!isSchoolMovedError(error)) return;
  const now = Date.now();
  if (now - lastShownAt < REPEAT_AFTER_MS) return;
  lastShownAt = now;

  const data = (error as { response: { data: MovedBody } }).response.data;
  const body = typeof data.error === "object" && data.error?.body;
  const where = data.region ? data.region.toUpperCase() : "another region";
  try {
    toast.warning(body || `This school has moved to ${where}. This copy is read only; switch region to make changes.`, {
      autoClose: 8000,
      position: "bottom-right",
    });
  } catch {
    // A toast that cannot render must not break the request's own handling.
  }
}

/** Test seam. */
export function resetSchoolMovedForTests(): void {
  lastShownAt = 0;
}
